import React, { FC, ReactNode, MouseEvent } from 'react'
import classnames from 'classnames/bind'
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard'
import type { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface CopyableTextProps extends AutomationTarget {
  text: string
  children?: ReactNode
  onCopy?: (text: string) => void
  onError?: (error: Error) => void
  className?: string
  disabled?: boolean
}

export const CopyableText: FC<CopyableTextProps> = ({
  text,
  children,
  onCopy,
  onError,
  className,
  disabled = false,
  elmId,
}) => {
  const { copy, status } = useCopyToClipboard({
    onSuccess: onCopy,
    onError,
    resetDelay: 1500, // Match the visual feedback duration
  })

  const handleClick = async (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      await copy(text)
    }
  }

  return (
    <span
      className={cx(
        'copyable-text',
        `copyable-text--${status}`,
        {
          'copyable-text--disabled': disabled,
        },
        className
      )}
      onClick={handleClick}
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-label='Click to copy'
      data-elm-id={elmId}
    >
      {children || text}
      {status === 'success' && (
        <span className={cx('copyable-text-feedback')}>Copied!</span>
      )}
    </span>
  )
}

export default CopyableText
